import { IsProblemResult } from "@/helpers/newUserSelector";
import { GenericController, PagedResult, User, UserFilter } from "@infoland/iprova-api";

export default {
    /**
     * Gets a list of users
     * @param filter (optional) A filter to use to find only specific users
     * @param offset (optional, default 0) The offset from which to start returning users from the list
     * @param maxResults (optional, default 100) The maximum users to return at one time
     * @param suppressErrors Whether to supress the throwing of exceptions on error
     * @returns a paged result of users
     */
    async getUsersAsync(
        filter: UserFilter = undefined,
        offset: number = 0,
        maxResults: number = 100,
        suppressErrors: boolean = false
    ): Promise<PagedResult<User> | null> {
        let path: string;
        const controller = new GenericController();

        if (filter && filterRequiresPost(filter)) {
            const filterResponse: any = await controller.postAsync<UserFilter, any>("/users/filter", filter, suppressErrors);

            if (!IsProblemResult(filterResponse)) {
                const filterId: string = filterResponse;
                path = `/users/filter/${filterId}?offset=${offset}&limit=${maxResults}&envelope=false`;
            } else return null;
        } else {
            path = `/users?offset=${offset}&limit=${maxResults}&envelope=false`;

            if (filter != undefined) {
                // add simple filters to the path
                if (filter.name) path += `&name=${encodeURIComponent(filter.name)}`;
                if (filter.login_code) path += `&login_code=${encodeURIComponent(filter.login_code)}`;
                if (filter.name_logincode_email_contains)
                    path += `&name_logincode_email_contains=${encodeURIComponent(filter.name_logincode_email_contains)}`;
                if (filter.email && filter.email.op === "pr") path += `&has_email=true`;
                if (filter.user_group_id) path += `&user_group_id=${encodeURIComponent(filter.user_group_id)}`;
                if (filter.active) path += `&is_active=true`;
            }
        }
        const userResponse: PagedResult<User> = await controller.getPagedResultAsync<User>(path, suppressErrors);

        if (!IsProblemResult(userResponse)) return userResponse;
        else return null;
    },

    /**
     * Gets the current user
     * @param suppressErrors Whether to supress the throwing of exceptions on error
     * @returns the current user
     */
    async getCurrentUserAsync(suppressErrors: boolean = false): Promise<User | null> {
        const controller = new GenericController();

        const userResponse: User = await controller.getAsync<User>("users/me", suppressErrors);

        if (!IsProblemResult(userResponse)) return userResponse;
        else return null;
    }
};

/**
 * Checks whether the given filter requires that a 'post' filter be used (ao because of url length limitations)
 * @param filter The filter to check
 * @returns true if the filter requires a POST request to be done first, otherwise false
 */
function filterRequiresPost(filter: UserFilter): boolean {
    // require a post for every filter with user ids
    if (filter.user_ids) return true;

    // require a post if login code and name are both provided
    let numberOfTextFilters: number = 0;
    const textFilters: (keyof UserFilter)[] = ["login_code", "name", "name_logincode_email_contains", "user_group_id"];

    for (let k: number = 0; k < textFilters.length; k++) {
        if (filter[textFilters[k]]) numberOfTextFilters++;
        if (numberOfTextFilters > 1) return true;
    }

    // if one of the text filters is longer than 50 characters (if there were 2 or more filters, this point is never reached), require a post
    if (((filter.login_code || "") + (filter.name || "") + (filter.name_logincode_email_contains || "")).length > 50) return true;

    // all other cases do not require a post filter
    return false;
}
